import { useMemo } from 'react'
import { Flex } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import type { AssetsValueChartProps } from '@/components/@Shared/AssetsValueChart/AssetsValueChart.types'
import { CategorizedAssets } from '@/components/@Shared/CategorizedAssets/CategorizedAssets'
import { CategorizedAssetsType } from '@/components/@Shared/CategorizedAssets/CategorizedAssets.types'
import { DonutChart } from '@/components/@Shared/DonutChart/DonutChart'
import { ShadowBox } from '@/components/@Shared/ShadowBox/ShadowBox'
import { ValueChartNoAccountsNotice } from '@/components/@Shared/ValueChartNoAccountsNotice/ValueChartNoAccountsNotice'
import { Loader } from '@/components/Loader/Loader'
import { Text } from '@/components/Text/Text'
import { Title } from '@/components/Title/Title'

import * as Common from '@/__generated__/queries/common'
import { AdvisorsService, BankingService } from '@/__generated__/requests'

import { formatMoney } from '@/helpers/formatMoney.helper'

export function AssetsValueChart({
  extended = false,
  userId,
}: AssetsValueChartProps) {
  const now = dayjs()
  const chartType = 'assets'
  const isUser = !!userId

  const queryFn = isUser
    ? () =>
        AdvisorsService.advisorUsersControllerGetDonutStats({
          type: chartType,
          userId: +userId,
        })
    : () =>
        BankingService.bankingControllerGetDonutStats({
          type: chartType,
        })

  const queryKey = isUser
    ? Common.UseAdvisorsServiceAdvisorUsersControllerGetDonutStatsKeyFn({
        type: chartType,
        userId: +userId,
      })
    : Common.UseBankingServiceBankingControllerGetDonutStatsKeyFn({
        type: chartType,
      })

  const { data, isFetching, isLoading } = useQuery({
    queryFn,
    queryKey,
    staleTime: 60 * 60 * 1000,
  })

  const chartData = useMemo(() => data ?? [], [data])

  const allAssetsValue = chartData.reduce((acc, { value }) => acc + value, 0)

  return (
    <ShadowBox
      w={{
        base: '100%',
      }}
    >
      {isFetching || isLoading ? (
        <Loader isMinimized />
      ) : (
        <>
          <Flex
            align="center"
            direction={{
              base: 'column',
              lg: 'row',
            }}
            justify="space-between"
          >
            <Title variant="h2">Asset Value</Title>

            <Title ml={8} variant="h1">
              {formatMoney(allAssetsValue, 'USD')}
            </Title>
          </Flex>

          {chartData.length > 0 ? (
            <>
              <DonutChart data={chartData} extended={extended} />

              {extended && (
                <CategorizedAssets
                  data={chartData}
                  type={CategorizedAssetsType.ASSETS}
                />
              )}
            </>
          ) : (
            <ValueChartNoAccountsNotice isUser={isUser} />
          )}

          {!extended && (
            <Text variant="body_2_black">As of {now.format('MMMM, YYYY')}</Text>
          )}
        </>
      )}
    </ShadowBox>
  )
}
